import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const http = axios.create({
  baseURL: API_BASE,
});

export const ctxHeaders = (session) => {
  const tenantId = session?.tenant?.id || "tenant_1";
  const userId = session?.user?.id || "public";
  const role = session?.user?.role || "client";
  return {
    "x-tenant-id": tenantId,
    "x-user-id": userId,
    "x-user-role": role,
  };
};

export const publicHeaders = () => ({
  "x-tenant-id": "tenant_1",
  "x-user-id": "public",
  "x-user-role": "client",
});

export const crmApi = {
  createLead: async (payload, session = null) => {
    const { data } = await http.post("/leads", payload, {
      headers: session ? ctxHeaders(session) : publicHeaders(),
    });
    return data;
  },

  listLeads: async (session) => {
    const { data } = await http.get("/leads", { headers: ctxHeaders(session) });
    return data;
  },

  markFirstResponse: async ({ lead_id }, session) => {
    const { data } = await http.post(`/leads/${lead_id}/first-response`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  convertLead: async ({ lead_id, assigned_to_user_id }, session) => {
    const params = new URLSearchParams();
    if (assigned_to_user_id) params.set("assigned_to_user_id", assigned_to_user_id);

    const { data } = await http.post(
      `/leads/${lead_id}/convert${params.toString() ? `?${params}` : ""}`,
      null,
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  listProspects: async ({ assigned_to_user_id } = {}, session) => {
    const params = new URLSearchParams();
    if (assigned_to_user_id) params.set("assigned_to_user_id", assigned_to_user_id);

    const { data } = await http.get(
      `/prospects${params.toString() ? `?${params}` : ""}`,
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  getProspect: async ({ prospect_id }, session) => {
    const { data } = await http.get(`/prospects/${prospect_id}`, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  updateProspect: async (payload, session) => {
    const { prospect_id, ...fields } = payload;
    const body = {};
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined) body[k] = v;
    });
    const { data } = await http.patch(
      `/prospects/${prospect_id}`,
      body,
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  addInteraction: async ({ prospect_id, type = "note", summary }, session) => {
    const { data } = await http.post(
      `/prospects/${prospect_id}/interactions`,
      { type, summary },
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  mockBookInitialCall: async ({ prospect_id }, session) => {
    const { data } = await http.post(
      `/prospects/${prospect_id}/meetings/mock-book`,
      null,
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  mockPayment: async ({ prospect_id, product_key }, session) => {
    const { data } = await http.post(
      `/prospects/${prospect_id}/payments/mock`,
      { product_key },
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  listNotifications: async (session) => {
    const { data } = await http.get("/notifications", {
      headers: ctxHeaders(session),
    });
    return data;
  },

  markNotificationRead: async ({ notification_id }, session) => {
    const { data } = await http.post(`/notifications/${notification_id}/read`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  enrichWithAI: async ({ prospect_id }, session) => {
    const { data } = await http.post(`/prospects/${prospect_id}/enrich-ai`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  // Tenant Provisioning
  listPendingTenants: async (session) => {
    const { data } = await http.get(`/tenants/pending`, { headers: ctxHeaders(session) });
    return data;
  },
  listProvisionedTenants: async (session) => {
    const { data } = await http.get(`/tenants/provisioned`, { headers: ctxHeaders(session) });
    return data;
  },
  provisionTenant: async ({ prospect_id }, session) => {
    const { data } = await http.post(`/tenants/provision/${prospect_id}`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },
  inviteToTenant: async ({ tenant_id }, session) => {
    const { data } = await http.post(`/tenants/${tenant_id}/invite`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },
  getTenantPortal: async ({ tenant_id }, session) => {
    const { data } = await http.get(`/tenants/${tenant_id}/portal`, {
      headers: ctxHeaders(session),
    });
    return data;
  },
};

export const assessmentApi = {
  getQuestionnaire: async (template_id, session = null) => {
    const { data } = await http.get(`/questionnaires/${template_id}`, {
      headers: session ? ctxHeaders(session) : publicHeaders(),
    });
    return data;
  },

  getResponseSet: async ({ prospect_id, template_id }, session) => {
    const { data } = await http.get(
      `/prospects/${prospect_id}/assessments/${template_id}`,
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  upsertResponse: async (
    { prospect_id, template_id, question_id, value, notes = "" },
    session,
  ) => {
    const { data } = await http.put(
      "/assessments/response",
      { prospect_id, template_id, question_id, value, notes },
      { headers: ctxHeaders(session) },
    );
    return data;
  },

  lockResponseSet: async ({ response_set_id }, session) => {
    const { data } = await http.post(`/assessments/${response_set_id}/lock`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  generateTalkingPoints: async ({ session_id }, session) => {
    const { data } = await http.post(`/assessments/sessions/${session_id}/generate-talking-points`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  regenerateSingleTalkingPoint: async ({ question_id }, session) => {
    const { data } = await http.post(`/assessments/questions/${question_id}/regenerate-talking-point`, null, {
      headers: ctxHeaders(session),
    });
    return data;
  },
};

export const siteCopyApi = {
  get: async () => {
    const { data } = await http.get("/site-copy", { headers: publicHeaders() });
    return data;
  },
  getAdmin: async (session) => {
    const { data } = await http.get("/site-copy", { headers: ctxHeaders(session) });
    return data;
  },
  update: async (content, session) => {
    const { data } = await http.put(
      "/site-copy",
      { content },
      { headers: ctxHeaders(session) },
    );
    return data;
  },
};

export const pilotApi = {
  getPublic: async () => {
    const { data } = await http.get("/pilot/config", { headers: publicHeaders() });
    return data;
  },
  getAdmin: async (session) => {
    const { data } = await http.get("/admin/pilot/config", { headers: ctxHeaders(session) });
    return data;
  },
  update: async (config, session) => {
    const { data } = await http.put("/admin/pilot/config", config, { headers: ctxHeaders(session) });
    return data;
  },
  schoolSearch: async (q, state = "") => {
    const { data } = await http.get("/pilot/school-search", { params: { q, state }, headers: publicHeaders() });
    return data.results || [];
  },
  submitSingleSchool: async (payload) => {
    const { data } = await http.post("/pilot/single-school", payload, { headers: publicHeaders() });
    return data;
  },
  submitPortfolio: async (payload) => {
    const { data } = await http.post("/pilot/portfolio", payload, { headers: publicHeaders() });
    return data;
  },
  participants: async (session) => {
    const { data } = await http.get("/admin/pilot/participants", { headers: ctxHeaders(session) });
    return data;
  },
  convert: async (kind, id, session) => {
    const { data } = await http.post("/admin/pilot/convert", { kind, id }, { headers: ctxHeaders(session) });
    return data;
  },
};

export const dataMaintenanceApi = {
  getInventory: async (session) => {
    const { data } = await http.get("/admin/data-maintenance/inventory", { headers: ctxHeaders(session) });
    return data;
  },
  getProtected: async (session) => {
    const { data } = await http.get("/admin/data-maintenance/protected", { headers: ctxHeaders(session) });
    return data;
  },
  searchTenants: async (q, session) => {
    const { data } = await http.get(`/admin/data-maintenance/tenants${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: ctxHeaders(session) });
    return data;
  },
  previewSchool: async (tenant_id, session) => {
    const { data } = await http.post("/admin/data-maintenance/single-school/preview", { tenant_id }, { headers: ctxHeaders(session) });
    return data;
  },
  deleteSchool: async (payload, session) => {
    const { data } = await http.post("/admin/data-maintenance/single-school/delete", payload, { headers: ctxHeaders(session) });
    return data;
  },
  bulkReset: async (payload, session) => {
    const { data } = await http.post("/admin/data-maintenance/bulk-reset", payload, { headers: ctxHeaders(session) });
    return data;
  },
  bulkResetStatus: async (job_id, session) => {
    const { data } = await http.get(`/admin/data-maintenance/bulk-reset/status/${job_id}`, { headers: ctxHeaders(session) });
    return data;
  },
  validate: async (session) => {
    const { data } = await http.get("/admin/data-maintenance/validate", { headers: ctxHeaders(session) });
    return data;
  },
  listJobs: async (session) => {
    const { data } = await http.get("/admin/data-maintenance/jobs", { headers: ctxHeaders(session) });
    return data;
  },
  listBackups: async (session) => {
    const { data } = await http.get("/admin/data-maintenance/backups", { headers: ctxHeaders(session) });
    return data;
  },
  directoryStatus: async (session) => {
    const { data } = await http.get("/admin/data-maintenance/directory/status", { headers: ctxHeaders(session) });
    return data;
  },
  rebuildDirectory: async (payload, session) => {
    const { data } = await http.post("/admin/data-maintenance/directory/rebuild", payload, { headers: ctxHeaders(session) });
    return data;
  },
  directoryRebuildStatus: async (job_id, session) => {
    const { data } = await http.get(`/admin/data-maintenance/directory/rebuild/status/${job_id}`, { headers: ctxHeaders(session) });
    return data;
  },
};

export const organizationApi = {
  list: async (params, session) => {
    const qs = new URLSearchParams(params || {}).toString();
    return (await http.get(`/admin/organizations${qs ? `?${qs}` : ""}`, { headers: ctxHeaders(session) })).data;
  },
  get: async (id, session) => (await http.get(`/admin/organizations/${id}`, { headers: ctxHeaders(session) })).data,
  create: async (payload, session) => (await http.post("/admin/organizations", payload, { headers: ctxHeaders(session) })).data,
  update: async (id, payload, session) => (await http.put(`/admin/organizations/${id}`, payload, { headers: ctxHeaders(session) })).data,
  assignSales: async (id, employee_id, session) => (await http.post(`/admin/organizations/${id}/assign-sales`, { employee_id }, { headers: ctxHeaders(session) })).data,
  deactivate: async (id, session) => (await http.post(`/admin/organizations/${id}/deactivate`, {}, { headers: ctxHeaders(session) })).data,
  merge: async (source_id, target_id, session) => (await http.post("/admin/organizations/merge", { source_id, target_id }, { headers: ctxHeaders(session) })).data,
  migrate: async (session) => (await http.post("/admin/organizations/migrate", {}, { headers: ctxHeaders(session) })).data,
  mine: async (headers) => (await http.get("/organizations/mine", { headers })).data,
  terminology: async () => (await http.get("/organizations/terminology")).data,
};

const slBase = "/admin/scenario-lab";
export const scenarioLabApi = {
  access: async (session) => (await http.get(`${slBase}/access`, { headers: ctxHeaders(session) })).data,
  listScenarios: async (session) => (await http.get(`${slBase}/scenarios`, { headers: ctxHeaders(session) })).data,
  createScenario: async (payload, session) => (await http.post(`${slBase}/scenarios`, payload, { headers: ctxHeaders(session) })).data,
  getScenario: async (id, session) => (await http.get(`${slBase}/scenarios/${id}`, { headers: ctxHeaders(session) })).data,
  resetScenario: async (id, session) => (await http.post(`${slBase}/scenarios/${id}/reset`, {}, { headers: ctxHeaders(session) })).data,
  deleteScenario: async (id, session) => (await http.delete(`${slBase}/scenarios/${id}`, { headers: ctxHeaders(session) })).data,
  validateScenario: async (id, session) => (await http.post(`${slBase}/scenarios/${id}/validate`, {}, { headers: ctxHeaders(session) })).data,
  generateStage: async (id, stage, payload, session) => (await http.post(`${slBase}/scenarios/${id}/stages/${stage}/generate`, payload || {}, { headers: ctxHeaders(session) })).data,
  manualStage: async (id, stage, session) => (await http.post(`${slBase}/scenarios/${id}/stages/${stage}/manual`, {}, { headers: ctxHeaders(session) })).data,
  jobStatus: async (id, jobId, session) => (await http.get(`${slBase}/scenarios/${id}/jobs/${jobId}`, { headers: ctxHeaders(session) })).data,
  listTemplates: async (session) => (await http.get(`${slBase}/templates`, { headers: ctxHeaders(session) })).data,
  saveTemplate: async (payload, session) => (await http.post(`${slBase}/templates`, payload, { headers: ctxHeaders(session) })).data,
  deleteTemplate: async (id, session) => (await http.delete(`${slBase}/templates/${id}`, { headers: ctxHeaders(session) })).data,
  restoreTemplate: async (id, session) => (await http.post(`${slBase}/templates/${id}/restore`, {}, { headers: ctxHeaders(session) })).data,
  setTour: async (id, enabled, session) => (await http.post(`${slBase}/scenarios/${id}/tour`, { enabled }, { headers: ctxHeaders(session) })).data,
  publicTour: async (token) => (await http.get(`/scenario-tour/${token}`)).data,
};

const sbxBase = "/admin/sandbox";
export const sandboxAdminApi = {
  listAccess: async (session) => (await http.get(`${sbxBase}/access`, { headers: ctxHeaders(session) })).data,
  setAccess: async (email, enabled, session) => (await http.post(`${sbxBase}/access`, { email, enabled }, { headers: ctxHeaders(session) })).data,
  candidates: async (q, session) => (await http.get(`${sbxBase}/candidates`, { params: { q: q || "" }, headers: ctxHeaders(session) })).data,
};

const intelBase = "/admin/intelligence";
export const intelligenceAdminApi = {
  listAccess: async (session) => (await http.get(`${intelBase}/access`, { headers: ctxHeaders(session) })).data,
  setAccess: async (scope_type, scope_id, enabled, session) => (await http.post(`${intelBase}/access`, { scope_type, scope_id, enabled }, { headers: ctxHeaders(session) })).data,
  candidates: async (q, session) => (await http.get(`${intelBase}/candidates`, { params: { q: q || "" }, headers: ctxHeaders(session) })).data,
};


export const referralApi = {
  context: async (headers) => (await http.get("/referrals/context", { headers })).data,
  send: async (payload, headers) => (await http.post("/referrals/send", payload, { headers })).data,
  mine: async (headers) => (await http.get("/referrals/mine", { headers })).data,
  track: async (ref, event) => (await http.get(`/referrals/track?ref=${ref}&event=${event}`)).data,
  adminList: async (session) => (await http.get("/admin/referrals", { headers: ctxHeaders(session) })).data,
  adminStats: async (session) => (await http.get("/admin/referrals/stats", { headers: ctxHeaders(session) })).data,
  getSettings: async (session) => (await http.get("/admin/referrals/settings", { headers: ctxHeaders(session) })).data,
  saveSettings: async (payload, session) => (await http.put("/admin/referrals/settings", payload, { headers: ctxHeaders(session) })).data,
};

export const appVersionApi = {
  get: async () => (await http.get("/app-version")).data,
  adminGet: async (session) => (await http.get("/admin/app-version", { headers: ctxHeaders(session) })).data,
  bump: async (session) => (await http.post("/admin/app-version/bump", {}, { headers: ctxHeaders(session) })).data,
  set: async (version, session) => (await http.put("/admin/app-version", { version }, { headers: ctxHeaders(session) })).data,
};

export const messageBarApi = {
  getActive: async (context, pilot = false) => {
    const params = new URLSearchParams({ context: context || "public", pilot: pilot ? "true" : "false" });
    const { data } = await http.get(`/message-bar/active?${params.toString()}`);
    return data;
  },
  list: async (session) => {
    const { data } = await http.get("/admin/message-bar", { headers: ctxHeaders(session) });
    return data;
  },
  create: async (payload, session) => {
    const { data } = await http.post("/admin/message-bar", payload, { headers: ctxHeaders(session) });
    return data;
  },
  update: async (id, payload, session) => {
    const { data } = await http.put(`/admin/message-bar/${id}`, payload, { headers: ctxHeaders(session) });
    return data;
  },
  remove: async (id, session) => {
    const { data } = await http.delete(`/admin/message-bar/${id}`, { headers: ctxHeaders(session) });
    return data;
  },
};

export const analyticsApi = {
  track: async (name, payload = {}, session = null) => {
    try {
      const { data } = await http.post(
        "/analytics/events",
        { name, payload },
        { headers: session ? ctxHeaders(session) : publicHeaders() },
      );
      return data;
    } catch {
      // Don’t block UI on analytics
      return null;
    }
  },
  list: async (session) => {
    const { data } = await http.get("/analytics/events", {
      headers: ctxHeaders(session),
    });
    return data;
  },
};


export const chatApi = {
  sendMessage: async (session_id, message, session = null) => {
    const { data } = await http.post(
      "/chat/message",
      { session_id, message },
      { headers: session ? ctxHeaders(session) : publicHeaders() },
    );
    return data;
  },

  getMessages: async (session_id, session = null) => {
    const { data } = await http.get(`/chat/sessions/${session_id}/messages`, {
      headers: session ? ctxHeaders(session) : publicHeaders(),
    });
    return data;
  },

  listSessions: async (session) => {
    const { data } = await http.get("/chat/sessions", {
      headers: ctxHeaders(session),
    });
    return data;
  },

  qualifySession: async (session_id, visitor_info, session) => {
    const params = new URLSearchParams();
    if (visitor_info.name) params.set("visitor_name", visitor_info.name);
    if (visitor_info.email) params.set("visitor_email", visitor_info.email);
    if (visitor_info.company) params.set("visitor_company", visitor_info.company);
    
    const { data } = await http.post(
      `/chat/sessions/${session_id}/qualify?${params}`,
      null,
      { headers: ctxHeaders(session) },
    );
    return data;
  },
};

export const emailDraftApi = {
  list: async (status, session) => {
    const params = status ? `?status=${status}` : "";
    const { data } = await http.get(`/email-drafts${params}`, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  get: async (draft_id, session) => {
    const { data } = await http.get(`/email-drafts/${draft_id}`, {
      headers: ctxHeaders(session),
    });
    return data;
  },

  action: async (draft_id, action, edited_body, session, edited_subject) => {
    const { data } = await http.post(
      `/email-drafts/${draft_id}/action`,
      { action, edited_body, edited_subject },
      { headers: ctxHeaders(session) },
    );
    return data;
  },
};
